/*
 * ============================================================
 * NORTA GLOBAL PRODUCT CAROUSEL AUTOPLAY
 * ============================================================
 *
 * Compatible con:
 *
 * 1. Carruseles nativos Horizon:
 *    slideshow-component
 *
 * 2. Carruseles personalizados Norta:
 *    overflow-x: auto / scroll
 *
 * Reconoce tarjetas:
 *
 *    .norta-card
 *    .product-card
 *
 * ============================================================
 */

const NORTA_PRODUCT_CARD_SELECTOR =
  '.norta-card, .product-card';

const NORTA_NATIVE_SLIDESHOW_SELECTOR =
  'slideshow-component';

const initializedNative =
  new WeakSet();

const initializedScroll =
  new WeakSet();

const nativeTimers =
  new WeakMap();

const scrollTimers =
  new WeakMap();


/* ============================================================
   CONFIGURACIÓN GLOBAL
   ============================================================ */

function getNortaAutoplayConfig() {
  const root =
    document.documentElement;

  return {
    enabled:
      root.dataset.nortaCardAutoplay === 'true',

    interval:
      Math.max(
        2,
        Number(
          root.dataset.nortaCardAutoplaySeconds || 4
        )
      ) * 1000,

    pauseHover:
      root.dataset.nortaCardPauseHover !== 'false',

    restartDelay:
      Math.max(
        1,
        Number(
          root.dataset.nortaCardRestartSeconds || 2
        )
      ) * 1000
  };
}


/* ============================================================
   REDUCED MOTION
   ============================================================ */

function nortaReducedMotion() {
  return window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;
}


/* ============================================================
   UTILIDADES
   ============================================================ */

function hasProductCards(element) {
  if (!element) {
    return false;
  }

  return Boolean(
    element.querySelector(
      NORTA_PRODUCT_CARD_SELECTOR
    )
  );
}


function clearNativeTimer(slideshow) {
  const state =
    nativeTimers.get(slideshow);

  if (!state) {
    return;
  }

  if (state.interval) {
    window.clearInterval(
      state.interval
    );

    state.interval = null;
  }

  if (state.resumeTimeout) {
    window.clearTimeout(
      state.resumeTimeout
    );

    state.resumeTimeout = null;
  }
}


function clearScrollTimer(carousel) {
  const state =
    scrollTimers.get(carousel);

  if (!state) {
    return;
  }

  if (state.interval) {
    window.clearInterval(
      state.interval
    );

    state.interval = null;
  }

  if (state.resumeTimeout) {
    window.clearTimeout(
      state.resumeTimeout
    );

    state.resumeTimeout = null;
  }
}


/* ============================================================
   HORIZON NATIVE SLIDESHOW
   ============================================================ */

function getNativeSlides(slideshow) {
  /*
   * Horizon expone slideshow.slides,
   * pero dejamos fallback por seguridad.
   */

  if (
    slideshow.slides &&
    slideshow.slides.length
  ) {
    return Array.from(
      slideshow.slides
    );
  }

  return Array.from(
    slideshow.querySelectorAll(
      'slideshow-slide'
    )
  );
}


function moveNativeSlideshow(slideshow) {
  const config =
    getNortaAutoplayConfig();

  const state =
    nativeTimers.get(slideshow);

  if (!state) {
    return;
  }


  if (!config.enabled) {
    clearNativeTimer(slideshow);
    return;
  }


  if (state.interacting) {
    return;
  }


  if (document.hidden) {
    return;
  }


  if (nortaReducedMotion()) {
    return;
  }


  const slides =
    getNativeSlides(slideshow);


  if (slides.length <= 1) {
    return;
  }


  /*
   * Usamos la API NATIVA de Horizon.
   *
   * Esto es mucho más estable que manipular
   * scrollLeft manualmente.
   */

  if (
    typeof slideshow.select !== 'function'
  ) {
    return;
  }


  const current =
    Number(slideshow.current || 0);

  const next =
    current + 1;


  /*
   * Al llegar al último producto,
   * regresamos al primero.
   */

  if (next >= slides.length) {
    slideshow.select(
      0,
      undefined,
      {
        animate: true
      }
    );

    return;
  }


  slideshow.select(
    next,
    undefined,
    {
      animate: true
    }
  );
}


function startNativeAutoplay(slideshow) {
  const config =
    getNortaAutoplayConfig();

  clearNativeTimer(slideshow);


  if (!config.enabled) {
    return;
  }


  if (nortaReducedMotion()) {
    return;
  }


  if (document.hidden) {
    return;
  }


  if (!hasProductCards(slideshow)) {
    return;
  }


  const slides =
    getNativeSlides(slideshow);


  if (slides.length <= 1) {
    return;
  }


  let state =
    nativeTimers.get(slideshow);


  if (!state) {
    state = {
      interval: null,
      resumeTimeout: null,
      interacting: false
    };

    nativeTimers.set(
      slideshow,
      state
    );
  }


  state.interval =
    window.setInterval(
      () => {
        moveNativeSlideshow(
          slideshow
        );
      },
      config.interval
    );
}


function pauseNative(slideshow) {
  const state =
    nativeTimers.get(slideshow);

  if (!state) {
    return;
  }


  state.interacting = true;


  if (state.interval) {
    window.clearInterval(
      state.interval
    );

    state.interval = null;
  }


  if (state.resumeTimeout) {
    window.clearTimeout(
      state.resumeTimeout
    );

    state.resumeTimeout = null;
  }
}


function resumeNative(slideshow) {
  const config =
    getNortaAutoplayConfig();

  const state =
    nativeTimers.get(slideshow);

  if (!state) {
    return;
  }


  if (state.resumeTimeout) {
    window.clearTimeout(
      state.resumeTimeout
    );
  }


  state.resumeTimeout =
    window.setTimeout(
      () => {
        state.interacting = false;

        startNativeAutoplay(
          slideshow
        );
      },
      config.restartDelay
    );
}


function initializeNativeSlideshow(
  slideshow
) {
  if (!slideshow) {
    return;
  }


  if (
    initializedNative.has(
      slideshow
    )
  ) {
    startNativeAutoplay(
      slideshow
    );

    return;
  }


  if (!hasProductCards(slideshow)) {
    return;
  }


  /*
   * Evitamos animar el carrusel de imágenes
   * que vive DENTRO de una tarjeta de producto.
   *
   * Solo queremos listas de productos.
   */

  if (
    slideshow.closest(
      NORTA_PRODUCT_CARD_SELECTOR
    )
  ) {
    return;
  }


  const slides =
    getNativeSlides(slideshow);


  if (slides.length <= 1) {
    return;
  }


  initializedNative.add(
    slideshow
  );


  nativeTimers.set(
    slideshow,
    {
      interval: null,
      resumeTimeout: null,
      interacting: false
    }
  );


  /* ----------------------------------------------------------
     HOVER
     ---------------------------------------------------------- */

  slideshow.addEventListener(
    'mouseenter',
    () => {
      const config =
        getNortaAutoplayConfig();

      if (
        config.pauseHover
      ) {
        pauseNative(
          slideshow
        );
      }
    }
  );


  slideshow.addEventListener(
    'mouseleave',
    () => {
      const config =
        getNortaAutoplayConfig();

      if (
        config.pauseHover
      ) {
        resumeNative(
          slideshow
        );
      }
    }
  );


  /* ----------------------------------------------------------
     TOUCH
     ---------------------------------------------------------- */

  slideshow.addEventListener(
    'touchstart',
    () => {
      pauseNative(
        slideshow
      );
    },
    {
      passive: true
    }
  );


  slideshow.addEventListener(
    'touchend',
    () => {
      resumeNative(
        slideshow
      );
    },
    {
      passive: true
    }
  );


  /* ----------------------------------------------------------
     POINTER
     ---------------------------------------------------------- */

  slideshow.addEventListener(
    'pointerdown',
    () => {
      pauseNative(
        slideshow
      );
    }
  );


  slideshow.addEventListener(
    'pointerup',
    () => {
      resumeNative(
        slideshow
      );
    }
  );


  slideshow.addEventListener(
    'pointercancel',
    () => {
      resumeNative(
        slideshow
      );
    }
  );


  /* ----------------------------------------------------------
     KEYBOARD
     ---------------------------------------------------------- */

  slideshow.addEventListener(
    'focusin',
    () => {
      pauseNative(
        slideshow
      );
    }
  );


  slideshow.addEventListener(
    'focusout',
    () => {
      resumeNative(
        slideshow
      );
    }
  );


  startNativeAutoplay(
    slideshow
  );
}


/* ============================================================
   CARRUSELES PERSONALIZADOS CON SCROLL HORIZONTAL
   ============================================================ */

function isHorizontalScrollCarousel(
  element
) {
  if (!element) {
    return false;
  }


  if (!hasProductCards(element)) {
    return false;
  }


  const styles =
    window.getComputedStyle(
      element
    );


  const overflowX =
    styles.overflowX;


  const canScroll =
    overflowX === 'auto' ||
    overflowX === 'scroll';


  const hasOverflow =
    element.scrollWidth >
    element.clientWidth + 5;


  return (
    canScroll &&
    hasOverflow
  );
}


function getDirectProductItems(
  carousel
) {
  const cards =
    Array.from(
      carousel.querySelectorAll(
        NORTA_PRODUCT_CARD_SELECTOR
      )
    );


  const items = [];


  cards.forEach(
    (card) => {
      let node =
        card;


      while (
        node.parentElement &&
        node.parentElement !== carousel
      ) {
        node =
          node.parentElement;
      }


      if (
        node.parentElement === carousel &&
        !items.includes(node)
      ) {
        items.push(node);
      }
    }
  );


  return items;
}


function moveScrollCarousel(
  carousel
) {
  const config =
    getNortaAutoplayConfig();

  const state =
    scrollTimers.get(carousel);


  if (!state) {
    return;
  }


  if (!config.enabled) {
    clearScrollTimer(
      carousel
    );

    return;
  }


  if (state.interacting) {
    return;
  }


  if (document.hidden) {
    return;
  }


  if (nortaReducedMotion()) {
    return;
  }


  const items =
    getDirectProductItems(
      carousel
    );


  if (items.length <= 1) {
    return;
  }


  const maxScroll =
    carousel.scrollWidth -
    carousel.clientWidth;


  /*
   * Estamos al final.
   */

  if (
    carousel.scrollLeft >=
    maxScroll - 10
  ) {
    carousel.scrollTo({
      left: 0,
      behavior: 'smooth'
    });

    return;
  }


  const firstOffset =
    items[0].offsetLeft;


  let nextItem =
    null;


  for (
    const item
    of items
  ) {
    const target =
      item.offsetLeft -
      firstOffset;


    if (
      target >
      carousel.scrollLeft + 10
    ) {
      nextItem =
        item;

      break;
    }
  }


  if (!nextItem) {
    carousel.scrollTo({
      left: 0,
      behavior: 'smooth'
    });

    return;
  }


  const destination =
    nextItem.offsetLeft -
    firstOffset;


  carousel.scrollTo({
    left:
      Math.min(
        destination,
        maxScroll
      ),

    behavior:
      'smooth'
  });
}


function startScrollAutoplay(
  carousel
) {
  const config =
    getNortaAutoplayConfig();


  clearScrollTimer(
    carousel
  );


  if (!config.enabled) {
    return;
  }


  if (nortaReducedMotion()) {
    return;
  }


  if (document.hidden) {
    return;
  }


  if (
    !isHorizontalScrollCarousel(
      carousel
    )
  ) {
    return;
  }


  let state =
    scrollTimers.get(
      carousel
    );


  if (!state) {
    state = {
      interval: null,
      resumeTimeout: null,
      interacting: false
    };


    scrollTimers.set(
      carousel,
      state
    );
  }


  state.interval =
    window.setInterval(
      () => {
        moveScrollCarousel(
          carousel
        );
      },
      config.interval
    );
}


function pauseScroll(
  carousel
) {
  const state =
    scrollTimers.get(
      carousel
    );


  if (!state) {
    return;
  }


  state.interacting =
    true;


  if (state.interval) {
    window.clearInterval(
      state.interval
    );

    state.interval =
      null;
  }


  if (state.resumeTimeout) {
    window.clearTimeout(
      state.resumeTimeout
    );

    state.resumeTimeout =
      null;
  }
}


function resumeScroll(
  carousel
) {
  const config =
    getNortaAutoplayConfig();

  const state =
    scrollTimers.get(
      carousel
    );


  if (!state) {
    return;
  }


  if (state.resumeTimeout) {
    window.clearTimeout(
      state.resumeTimeout
    );
  }


  state.resumeTimeout =
    window.setTimeout(
      () => {
        state.interacting =
          false;

        startScrollAutoplay(
          carousel
        );
      },
      config.restartDelay
    );
}


function initializeScrollCarousel(
  carousel
) {
  if (!carousel) {
    return;
  }


  /*
   * Si pertenece a un slideshow nativo Horizon,
   * no lo inicializamos dos veces.
   */

  if (
    carousel.closest(
      NORTA_NATIVE_SLIDESHOW_SELECTOR
    )
  ) {
    return;
  }


  if (
    !isHorizontalScrollCarousel(
      carousel
    )
  ) {
    return;
  }


  if (
    initializedScroll.has(
      carousel
    )
  ) {
    startScrollAutoplay(
      carousel
    );

    return;
  }


  initializedScroll.add(
    carousel
  );


  scrollTimers.set(
    carousel,
    {
      interval: null,
      resumeTimeout: null,
      interacting: false
    }
  );


  carousel.addEventListener(
    'mouseenter',
    () => {
      const config =
        getNortaAutoplayConfig();

      if (
        config.pauseHover
      ) {
        pauseScroll(
          carousel
        );
      }
    }
  );


  carousel.addEventListener(
    'mouseleave',
    () => {
      const config =
        getNortaAutoplayConfig();

      if (
        config.pauseHover
      ) {
        resumeScroll(
          carousel
        );
      }
    }
  );


  carousel.addEventListener(
    'touchstart',
    () => {
      pauseScroll(
        carousel
      );
    },
    {
      passive: true
    }
  );


  carousel.addEventListener(
    'touchend',
    () => {
      resumeScroll(
        carousel
      );
    },
    {
      passive: true
    }
  );


  carousel.addEventListener(
    'pointerdown',
    () => {
      pauseScroll(
        carousel
      );
    }
  );


  carousel.addEventListener(
    'pointerup',
    () => {
      resumeScroll(
        carousel
      );
    }
  );


  carousel.addEventListener(
    'pointercancel',
    () => {
      resumeScroll(
        carousel
      );
    }
  );


  carousel.addEventListener(
    'focusin',
    () => {
      pauseScroll(
        carousel
      );
    }
  );


  carousel.addEventListener(
    'focusout',
    () => {
      resumeScroll(
        carousel
      );
    }
  );


  startScrollAutoplay(
    carousel
  );
}


/* ============================================================
   BUSCAR CARRUSELES HORIZON
   ============================================================ */

function scanNativeSlideshows(
  scope = document
) {
  const slideshows =
    [];


  if (
    scope instanceof Element &&
    scope.matches(
      NORTA_NATIVE_SLIDESHOW_SELECTOR
    )
  ) {
    slideshows.push(
      scope
    );
  }


  scope
    .querySelectorAll?.(
      NORTA_NATIVE_SLIDESHOW_SELECTOR
    )
    .forEach(
      (slideshow) => {
        slideshows.push(
          slideshow
        );
      }
    );


  slideshows.forEach(
    (slideshow) => {
      initializeNativeSlideshow(
        slideshow
      );
    }
  );
}


/* ============================================================
   BUSCAR CARRUSELES PERSONALIZADOS
   ============================================================ */

function scanScrollCarousels(
  scope = document
) {
  const cards =
    [];


  if (
    scope instanceof Element &&
    scope.matches(
      NORTA_PRODUCT_CARD_SELECTOR
    )
  ) {
    cards.push(
      scope
    );
  }


  scope
    .querySelectorAll?.(
      NORTA_PRODUCT_CARD_SELECTOR
    )
    .forEach(
      (card) => {
        cards.push(
          card
        );
      }
    );


  const candidates =
    new Set();


  cards.forEach(
    (card) => {
      /*
       * Si la tarjeta está dentro de un slideshow Horizon,
       * ese slideshow ya lo maneja la lógica nativa.
       */

      if (
        card.closest(
          NORTA_NATIVE_SLIDESHOW_SELECTOR
        )
      ) {
        return;
      }


      let parent =
        card.parentElement;

      let levels =
        0;


      while (
        parent &&
        parent !== document.body &&
        levels < 10
      ) {
        if (
          isHorizontalScrollCarousel(
            parent
          )
        ) {
          candidates.add(
            parent
          );

          break;
        }


        parent =
          parent.parentElement;

        levels++;
      }
    }
  );


  candidates.forEach(
    (carousel) => {
      initializeScrollCarousel(
        carousel
      );
    }
  );
}


/* ============================================================
   ESCANEO GLOBAL
   ============================================================ */

function scanNortaProductCarousels(
  scope = document
) {
  const config =
    getNortaAutoplayConfig();


  if (!config.enabled) {
    return;
  }


  scanNativeSlideshows(
    scope
  );


  scanScrollCarousels(
    scope
  );
}


/* ============================================================
   INICIALIZACIÓN
   ============================================================ */

async function initializeNortaAutoplay() {
  /*
   * Esperamos a que Horizon registre
   * su Web Component de slideshow.
   */

  if (
    window.customElements &&
    customElements.get(
      'slideshow-component'
    ) === undefined
  ) {
    try {
      await customElements.whenDefined(
        'slideshow-component'
      );
    } catch (error) {
      /*
       * Si por algún motivo no existe,
       * seguimos con los carruseles custom.
       */
    }
  }


  /*
   * Esperamos un frame para asegurarnos de
   * tener las medidas finales del layout.
   */

  window.requestAnimationFrame(
    () => {
      window.requestAnimationFrame(
        () => {
          scanNortaProductCarousels(
            document
          );
        }
      );
    }
  );
}


/* ============================================================
   DOM READY
   ============================================================ */

if (
  document.readyState === 'loading'
) {
  document.addEventListener(
    'DOMContentLoaded',
    initializeNortaAutoplay,
    {
      once: true
    }
  );
} else {
  initializeNortaAutoplay();
}


/* ============================================================
   CONTENIDO DINÁMICO SHOPIFY
   ============================================================ */

const nortaObserver =
  new MutationObserver(
    (mutations) => {
      const config =
        getNortaAutoplayConfig();


      if (!config.enabled) {
        return;
      }


      let shouldRescan =
        false;


      for (
        const mutation
        of mutations
      ) {
        if (
          mutation.addedNodes.length
        ) {
          shouldRescan =
            true;

          break;
        }
      }


      if (!shouldRescan) {
        return;
      }


      window.requestAnimationFrame(
        () => {
          scanNortaProductCarousels(
            document
          );
        }
      );
    }
  );


nortaObserver.observe(
  document.documentElement,
  {
    childList: true,
    subtree: true
  }
);


/* ============================================================
   CAMBIO DE VISIBILIDAD
   ============================================================ */

document.addEventListener(
  'visibilitychange',
  () => {
    if (document.hidden) {
      document
        .querySelectorAll(
          NORTA_NATIVE_SLIDESHOW_SELECTOR
        )
        .forEach(
          clearNativeTimer
        );


      document
        .querySelectorAll(
          '*'
        )
        .forEach(
          (element) => {
            if (
              initializedScroll.has(
                element
              )
            ) {
              clearScrollTimer(
                element
              );
            }
          }
        );

      return;
    }


    scanNortaProductCarousels(
      document
    );
  }
);


/* ============================================================
   SHOPIFY THEME EDITOR
   ============================================================ */

document.addEventListener(
  'shopify:section:load',
  () => {
    window.setTimeout(
      () => {
        scanNortaProductCarousels(
          document
        );
      },
      250
    );
  }
);


document.addEventListener(
  'shopify:section:select',
  () => {
    window.setTimeout(
      () => {
        scanNortaProductCarousels(
          document
        );
      },
      100
    );
  }
);


document.addEventListener(
  'shopify:block:select',
  () => {
    window.setTimeout(
      () => {
        scanNortaProductCarousels(
          document
        );
      },
      100
    );
  }
);


/* ============================================================
   RESIZE
   ============================================================ */

let nortaResizeTimer =
  null;


window.addEventListener(
  'resize',
  () => {
    if (
      nortaResizeTimer
    ) {
      window.clearTimeout(
        nortaResizeTimer
      );
    }


    nortaResizeTimer =
      window.setTimeout(
        () => {
          scanNortaProductCarousels(
            document
          );
        },
        250
      );
  },
  {
    passive: true
  }
);